import React, { useEffect, useRef, useState } from 'react';
import { Box, HStack, Input, Spinner, FormControl, FormLabel, List, ListItem, Text, Portal } from '@chakra-ui/react';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const SearchableSelect = ({
  label,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  fetchUrl, // e.g. '/api/users/clients?q='
  fetchUrls, // optional: array of urls to merge results from
  valueKey = 'id',
  labelKey = 'name',
  fallbackLabelKey,
  minChars = 0,
  size = 'md',
  isRequired = false,
  returnObject = false,
  clearOnSelect = false,
  isDisabled = false,
  allowCreate = false,
  onCreateOption,
}) => {
  const { t } = useLanguage();
  const { formatCurrency } = useCurrency();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedQ = useDebouncedValue(query, 300);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Initialize query with value if provided
  useEffect(() => {
    if (value && returnObject && value[labelKey]) {
      setQuery(value[labelKey]);
    } else if (value && !returnObject) {
      // Find the option by value and set query
      if (options.length > 0) {
        const option = options.find(opt => String(opt[valueKey]) === String(value));
        if (option) {
          setQuery(option[labelKey]);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, returnObject, valueKey, labelKey]);

  // Update query when options change and we have a value
  useEffect(() => {
    if (value && !returnObject && options.length > 0) {
      const option = options.find(opt => String(opt[valueKey]) === String(value));
      if (option) {
        setQuery(option[labelKey]);
      }
    }
  }, [options, value, returnObject, valueKey, labelKey]);

  useEffect(() => {
    let cancelled = false;
    const extractList = (data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.clients)) return data.clients;
      if (Array.isArray(data?.templates)) return data.templates;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.services)) return data.services;
      return [];
    };
    const normalizeItem = (item, source) => {
      const normalized = { ...item, __source: source };
      if (normalized.unit_price === undefined && normalized.base_price !== undefined) {
        normalized.unit_price = normalized.base_price;
      }
      return normalized;
    };
    const load = async () => {
      try {
        setLoading(true);
        const sources = Array.isArray(fetchUrls) && fetchUrls.length > 0 ? fetchUrls : (fetchUrl ? [fetchUrl] : []);
        if (sources.length === 0) {
          if (!cancelled) setOptions([]);
          return;
        }
        const qs = encodeURIComponent(debouncedQ || '');
        const requests = sources.map((base) => api.get(`${base}${qs}`).then(r => ({ source: base, data: r.data?.data })).catch(() => ({ source: base, data: [] })));
        const results = await Promise.all(requests);
        let merged = [];
        for (const r of results) {
          const list = extractList(r.data);
          merged = merged.concat(list.map(it => normalizeItem(it, r.source)));
        }
        if (!cancelled) setOptions(merged);
      } catch (_) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (debouncedQ.length >= minChars) {
      load();
    } else {
      setOptions([]);
    }
    return () => { cancelled = true; };
  }, [debouncedQ, fetchUrl, fetchUrls, minChars]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <FormControl isRequired={isRequired} ref={containerRef}>
      {label && <FormLabel>{label}</FormLabel>}
      <Box position="relative" overflow="visible">
        <HStack>
          <Input
            placeholder={searchPlaceholder || t('common.search', { defaultValue: 'Search...'})}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            size={size}
            autoComplete="off"
            isDisabled={isDisabled}
          />
          {loading && <Spinner size="sm" />}
        </HStack>
        {open && (
          <Portal>
            <Box 
              position="fixed"
              top={containerRef.current ? containerRef.current.getBoundingClientRect().bottom + 5 : 0}
              left={containerRef.current ? containerRef.current.getBoundingClientRect().left : 0}
              width={containerRef.current ? containerRef.current.offsetWidth : 'auto'}
              bg="white" 
              borderWidth="1px" 
              borderColor="gray.200"
              borderRadius="md" 
              zIndex={9999} 
              maxH="300px" 
              overflowY="auto" 
              boxShadow="xl"
              border="2px solid blue.300"
            >
              <List spacing={0}>
                {(!Array.isArray(options) || options.length === 0) && !loading && (
                  <ListItem p={3} fontSize="sm" color="gray.500" textAlign="center">
                    {query.length >= minChars 
                      ? t('messages.no_results_found', { defaultValue: 'No results found' }) 
                      : t('messages.type_to_search', { params: { minChars }, defaultValue: `Type at least ${minChars} characters to search`})}
                  </ListItem>
                )}
                {allowCreate && query.length >= minChars && !loading && (
                  <ListItem 
                    p={3}
                    fontSize="sm"
                    color="blue.700"
                    textAlign="center"
                    cursor="pointer"
                    _hover={{ bg: 'blue.50' }}
                    borderTop="1px solid"
                    borderColor="gray.100"
                    onClick={() => {
                      if (typeof onCreateOption === 'function') {
                        onCreateOption(String(query).trim());
                      }
                      if (clearOnSelect) setQuery('');
                      setOpen(false);
                    }}
                  >
                    {t('inventory.create_item', { defaultValue: 'Create item' })}: "{String(query).trim()}"
                  </ListItem>
                )}
                {(Array.isArray(options) ? options : []).map((opt, index) => (
                  <ListItem 
                    key={`${opt.__source || 'default'}-${opt[valueKey]}`} 
                    p={3} 
                    _hover={{ bg: 'blue.50', color: 'blue.700' }} 
                    cursor="pointer"
                    borderBottom={index < options.length - 1 ? "1px solid" : "none"}
                    borderColor="gray.100"
                    transition="all 0.2s"
                    onClick={() => {
                      const ret = returnObject ? opt : String(opt[valueKey]);
                      onChange(ret);
                      setQuery(clearOnSelect ? '' : (opt[labelKey] || ''));
                      setOpen(false);
                    }}
                  >
                    <Box>
                      <Text fontWeight="medium" fontSize="sm">
                        {opt[labelKey] || (fallbackLabelKey ? opt[fallbackLabelKey] : '')}
                      </Text>
                      {opt.category && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {t('common.category', {defaultValue: 'Category'})}: {opt.category}
                        </Text>
                      )}
                      {opt.unit_price !== undefined && (
                        <Text fontSize="xs" color="green.600" mt={1}>
                          {t('common.price', {defaultValue: 'Price'})}: {formatCurrency(opt.unit_price)}
                        </Text>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Portal>
        )}
      </Box>
    </FormControl>
  );
};

export default SearchableSelect;


